/*
 * RAZRWING / Shopper Momma — Matilda dual advertising v2.1
 * Install as: assets/rw-dual-ad.js (JAVASCRIPT, not an HTML section).
 * The interface HTML belongs in: snippets/rw-dual-ad.liquid.
 * No automatic audio; motion defaults to user-initiated (data-autoplay="false").
 * No sponsor is displayed unless name + link are supplied.
 */
(() => {
  'use strict';

  if (window.RWDualAd && typeof window.RWDualAd.mount === 'function') {
    window.RWDualAd.mount();
    return;
  }

  const initialized = new WeakSet();
  const controllers = new WeakMap();
  const desktopQuery = '(min-width: 1100px) and (min-height: 650px) and (prefers-reduced-motion: no-preference)';

  function permittedUrl(raw) {
    if (!raw || !String(raw).trim()) return '';
    try {
      const parsed = new URL(String(raw).trim(), window.location.origin);
      return /^https?:$/.test(parsed.protocol) ? parsed.href : '';
    } catch (_) {
      return '';
    }
  }

  function positiveSeconds(raw, fallback, max = 86400) {
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 && value <= max ? value : fallback;
  }

  function isSafariBrowser() {
    const ua = navigator.userAgent || '';
    return /Safari\//.test(ua) && !/(Chrome|Chromium|CriOS|Edg|OPR|FxiOS|Android)/.test(ua);
  }

  function signal(root, action, destination = '') {
    /* No network tracking is performed. Listeners can attach to this event. */
    root.dispatchEvent(new CustomEvent('rwda:interaction', {
      bubbles: true,
      detail: {
        action,
        destination,
        campaign: root.dataset.campaign || '',
        tier: root.dataset.tier || ''
      }
    }));
  }

  function init(root) {
    if (initialized.has(root)) return;
    const one = (selector) => root.querySelector(selector);
    const stage = one('.rwda__stage');
    const avatar = one('.rwda__avatar');
    const plane = one('.rwda__plane');
    const primaryTile = one('[data-creative="primary"]');
    const sponsorTile = one('[data-creative="sponsor"]');
    const dock = one('.rwda__dock');
    const reopen = one('[data-action="reopen"]');
    const close = one('[data-action="close"]');
    const play = one('[data-action="play"]');
    const pause = one('[data-action="pause"]');
    const status = one('.rwda__status');
    const primaryLink = one('[data-destination="primary"]');
    const sponsorLink = one('[data-destination="sponsor"]');
    const sponsorLabel = one('[data-sponsor-label]');
    const extraLink = one('[data-destination="extra"]');
    if (!stage || !avatar || !plane || !primaryTile || !sponsorTile || !dock ||
        !reopen || !close || !play || !pause || !status || !primaryLink ||
        !sponsorLink || !sponsorLabel || !extraLink) {
      console.warn('RAZRWING Matilda: missing required snippet elements.');
      return;
    }

    initialized.add(root);

    const primaryVideo = primaryTile.querySelector('video');
    const sponsorVideo = sponsorTile.querySelector('video');
    if (!primaryVideo || !sponsorVideo) return;

    const media = {
      desktop: permittedUrl(root.dataset.desktop),
      safari: permittedUrl(root.dataset.safari),
      primary: permittedUrl(root.dataset.primaryMedia),
      sponsor: permittedUrl(root.dataset.sponsorMedia)
    };
    const links = {
      primary: permittedUrl(root.dataset.primaryUrl) || '/collections/shopper-momma-shop',
      sponsor: permittedUrl(root.dataset.sponsorUrl),
      extra: permittedUrl(root.dataset.extraUrl)
    };
    const sponsorName = (root.dataset.sponsorName || '').trim();
    const sponsorActive = Boolean(sponsorName && links.sponsor);
    const sponsorCreativeActive = Boolean(sponsorActive && media.sponsor);
    const startTime = positiveSeconds(root.dataset.adStart, 0.03, 3600);
    const endTime = Math.max(startTime, positiveSeconds(root.dataset.adEnd, 5.06, 3600));
    const returnSeconds = positiveSeconds(root.dataset.returnSeconds, 240, 86400);
    let returnTimer = 0;
    let avatarFallbackTried = false;
    let primaryFailed = false;
    let sponsorFailed = false;
    let playbackPending = false;
    let offerClosed = false;

    primaryLink.href = links.primary;
    sponsorLabel.textContent = sponsorName;
    sponsorLink.hidden = !sponsorActive;
    sponsorTile.hidden = !sponsorCreativeActive;
    if (sponsorActive) sponsorLink.href = links.sponsor;
    else sponsorLink.removeAttribute('href');
    plane.classList.toggle('rwda__plane--has-sponsor', sponsorCreativeActive);
    extraLink.hidden = !links.extra;
    if (links.extra) {
      extraLink.href = links.extra;
      extraLink.textContent = (root.dataset.extraLabel || 'Featured campaign').trim() || 'Featured campaign';
    } else {
      extraLink.removeAttribute('href');
    }

    function setStatus(message) { status.textContent = message; }
    function canShowAnimation() { return !window.matchMedia || window.matchMedia(desktopQuery).matches; }

    function stopAdVideos(reset = false) {
      for (const video of [primaryVideo, sponsorVideo]) {
        video.pause();
        if (reset) {
          try { video.currentTime = 0; } catch (_) { /* metadata may not be loaded */ }
        }
      }
      plane.hidden = true;
    }

    function syncAdWindow() {
      const visible = !stage.hidden && !avatar.paused &&
        avatar.currentTime >= startTime && avatar.currentTime <= endTime;
      if (!visible) {
        stopAdVideos(false);
        return;
      }
      plane.hidden = (!media.primary || primaryFailed) && (!sponsorCreativeActive || sponsorFailed);
      primaryTile.hidden = !media.primary || primaryFailed;
      sponsorTile.hidden = !sponsorCreativeActive || sponsorFailed;
      plane.classList.toggle('rwda__plane--has-sponsor', sponsorCreativeActive && !sponsorFailed && !primaryFailed);
      if (media.primary && !primaryFailed) {
        const promise = primaryVideo.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      }
      if (sponsorCreativeActive && !sponsorFailed) {
        const promise = sponsorVideo.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      }
    }

    function setPlaybackControls(playing) {
      play.hidden = playing;
      pause.hidden = !playing;
    }

    function stopAnimation(reset = true) {
      playbackPending = false;
      avatar.pause();
      stopAdVideos(reset);
      if (reset) {
        try { avatar.currentTime = 0; } catch (_) { /* may not have metadata */ }
        stage.hidden = true;
      }
      setPlaybackControls(false);
    }

    function failAnimation(message) {
      stopAnimation(true);
      setStatus(message);
      signal(root, 'media_error');
    }

    function configureVideo(video, src) {
      if (!src || video.getAttribute('src') === src) return;
      video.src = src;
      video.load();
    }

    function candidateAvatarUrl() {
      return (isSafariBrowser() ? media.safari || media.desktop : media.desktop || media.safari);
    }

    async function beginAnimation() {
      if (playbackPending) return;
      if (!canShowAnimation()) {
        setStatus('Matilda video is available on larger screens with motion enabled. Shop anytime below.');
        return;
      }
      if (!candidateAvatarUrl()) {
        setStatus('Matilda video is not configured. You can still shop the collection.');
        return;
      }
      /* If the customer chooses Play, clear the right-hand space occupied by the AI Bot. */
      if (document.querySelector('#ssai-root.is-open') &&
          window.StorylineStoreAiBot && typeof window.StorylineStoreAiBot.close === 'function') {
        window.StorylineStoreAiBot.close();
      }
      playbackPending = true;
      avatarFallbackTried = false;
      try {
        configureVideo(avatar, candidateAvatarUrl());
        if (media.primary) configureVideo(primaryVideo, media.primary);
        if (sponsorCreativeActive) configureVideo(sponsorVideo, media.sponsor);
        stage.hidden = false;
        if (avatar.ended) avatar.currentTime = 0;
        avatar.muted = true;
        avatar.playsInline = true;
        await avatar.play();
        setPlaybackControls(true);
        setStatus('Matilda is playing. Select Pause to stop the animation.');
        signal(root, 'play');
        syncAdWindow();
      } catch (error) {
        /* A media error handler may already have initiated the fallback. */
        if (!avatarFallbackTried && candidateAvatarUrl() !== (isSafariBrowser() ? media.desktop : media.safari)) {
          const fallback = isSafariBrowser() ? media.desktop : media.safari;
          if (fallback) {
            avatarFallbackTried = true;
            configureVideo(avatar, fallback);
            try {
              await avatar.play();
              setPlaybackControls(true);
              setStatus('Matilda is playing.');
              signal(root, 'play');
              syncAdWindow();
              return;
            } catch (_) { /* expose a clean fallback message */ }
          }
        }
        failAnimation('Matilda video could not be played. You can still shop the collection.');
      } finally {
        playbackPending = false;
      }
    }

    function pauseAnimation() {
      avatar.pause();
      stopAdVideos(false);
      setPlaybackControls(false);
      setStatus('Matilda is paused. Select Play Matilda to continue.');
      signal(root, 'pause');
    }

    function clearReturnTimer() {
      if (returnTimer) window.clearTimeout(returnTimer);
      returnTimer = 0;
    }

    function openOffers(focus = false) {
      clearReturnTimer();
      offerClosed = false;
      dock.hidden = false;
      reopen.hidden = true;
      reopen.setAttribute('aria-expanded', 'true');
      if (focus) close.focus();
      signal(root, 'open');
    }

    function closeOffers() {
      clearReturnTimer();
      offerClosed = true;
      stopAnimation(true);
      dock.hidden = true;
      reopen.hidden = false;
      reopen.setAttribute('aria-expanded', 'false');
      reopen.focus();
      signal(root, 'close');
      /* data-return-seconds controls when the unobtrusive dock returns. */
      if (returnSeconds > 0) {
        returnTimer = window.setTimeout(() => {
          if (offerClosed && root.isConnected) openOffers(false);
        }, returnSeconds * 1000);
      }
    }

    avatar.addEventListener('timeupdate', syncAdWindow);
    avatar.addEventListener('seeked', syncAdWindow);
    avatar.addEventListener('ended', () => {
      stopAnimation(true);
      setStatus('Matilda has finished. You can replay or shop the collection.');
      signal(root, 'completed');
    });
    avatar.addEventListener('error', () => {
      /* The catch path in beginAnimation tries the alternative codec/format. */
      if (!playbackPending) failAnimation('Matilda video is currently unavailable. You can still shop.');
    });
    primaryVideo.addEventListener('error', () => {
      primaryFailed = true;
      primaryTile.hidden = true;
      plane.hidden = !sponsorCreativeActive || sponsorFailed;
      plane.classList.remove('rwda__plane--has-sponsor');
      console.warn('RAZRWING Matilda: primary ad video failed to load.');
    });
    sponsorVideo.addEventListener('error', () => {
      sponsorFailed = true;
      sponsorTile.hidden = true;
      plane.classList.remove('rwda__plane--has-sponsor');
      console.warn('RAZRWING Matilda: optional sponsor video failed to load.');
    });

    play.addEventListener('click', beginAnimation);
    pause.addEventListener('click', pauseAnimation);
    close.addEventListener('click', closeOffers);
    reopen.addEventListener('click', () => openOffers(true));
    for (const [element, destination] of [
      [primaryLink, 'primary'], [sponsorLink, 'sponsor'], [extraLink, 'extra']
    ]) {
      element.addEventListener('click', () => signal(root, 'click', destination));
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && !avatar.paused) pauseAnimation();
    });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(root)) {
        clearReturnTimer();
        stopAnimation(true);
      }
    });

    const controller = { open: () => openOffers(false), close: closeOffers, play: beginAnimation, pause: pauseAnimation };
    controllers.set(root, controller);
    openOffers(false);
    if (root.dataset.autoplay === 'true' && canShowAnimation()) beginAnimation();
  }

  function mount(scope = document) {
    if (scope.matches && scope.matches('[data-rwda]')) init(scope);
    scope.querySelectorAll('[data-rwda]').forEach(init);
  }

  window.RWDualAd = {
    mount,
    getController(root = document.querySelector('[data-rwda]')) {
      return root ? controllers.get(root) || null : null;
    }
  };
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }
})();